import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CheckCircle2, Circle, Loader2, X } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  addSection,
  createCommonLiveTasks,
  createGroupLiveTasks,
  createIndependentLiveTasks,
  getAllBlocksByProject,
  getAllWorkTypes,
  getUnitsByProjectId
} from '../../services/api/implementation/impl';
import { formatAiApiError } from './AiUiShared';
import { buildOpsAiReturnTo, openBoqWithReturn } from './opsAiNavigation';
import { navigateWithReturn } from '../../../shared/navigation';

/** Where drawing-required work of this template type is quantified in BOQ & BOM. */
const BOQ_BUCKET_BY_TYPE = {
  common: {
    label: 'Common Area',
    hint: 'Open the Common Area card, add drawings, then generate BOQ → BOM.'
  },
  independent: {
    label: 'Independent Accommodation',
    hint: 'Open the matching Independent accommodation, add drawings, then generate BOQ → BOM.'
  },
  group: {
    label: 'Group Accommodation / Block',
    hint: 'Open the Group accommodation or block, add drawings, then generate BOQ → BOM.'
  }
};

function Step({ done, active, title, children }) {
  return (
    <div className="flex gap-2.5">
      <div className="mt-0.5 shrink-0">
        {done ? (
          <CheckCircle2 className="h-4 w-4 text-teal-600" />
        ) : (
          <Circle className={`h-4 w-4 ${active ? 'text-indigo-500' : 'text-slate-300'}`} />
        )}
      </div>
      <div className="flex-1">
        <p
          className={`text-xs font-semibold ${
            done ? 'text-slate-500 line-through' : active ? 'text-slate-900' : 'text-slate-500'
          }`}
        >
          {title}
        </p>
        {children ? <div className="mt-1.5">{children}</div> : null}
      </div>
    </div>
  );
}

/**
 * After AI Apply: an on-screen plan — create live tasks, then (when tasks need
 * drawings) add BOQ/BOM in the correct bucket, then run on site.
 */
export default function OpsLiveCreatePanel({
  open,
  onClose,
  projectId,
  templateName,
  templateType = 'common',
  workTypeIds = [],
  hasDrawings = false,
  drawingTaskCount = 0
}) {
  const type = String(templateType || 'common').toLowerCase();
  const navigate = useNavigate();
  const location = useLocation();
  const [loadingMeta, setLoadingMeta] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const [units, setUnits] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [workTypes, setWorkTypes] = useState([]);
  const [unitId, setUnitId] = useState('');
  const [blockId, setBlockId] = useState('');
  const [sections, setSections] = useState(1);
  const [autoTried, setAutoTried] = useState(false);
  const [liveCreated, setLiveCreated] = useState(false);

  const bucket = BOQ_BUCKET_BY_TYPE[type] || BOQ_BUCKET_BY_TYPE.common;
  const drawingCount = Number(drawingTaskCount) || (hasDrawings ? 1 : 0);
  const needsDrawings = hasDrawings || drawingCount > 0;

  const selectedWorkTypes = useMemo(() => {
    const idSet = new Set((workTypeIds || []).map(Number).filter(Boolean));
    if (!idSet.size) return workTypes;
    return workTypes.filter((w) => idSet.has(Number(w.id)));
  }, [workTypes, workTypeIds]);

  const sectionedCommon = useMemo(
    () => selectedWorkTypes.filter((w) => w.sections === true || w.sections === 'true'),
    [selectedWorkTypes]
  );
  const plainCommon = useMemo(
    () => selectedWorkTypes.filter((w) => !(w.sections === true || w.sections === 'true')),
    [selectedWorkTypes]
  );

  useEffect(() => {
    if (!open || !projectId) return;
    let cancelled = false;
    (async () => {
      setLoadingMeta(true);
      setError('');
      setStatus('');
      setAutoTried(false);
      setLiveCreated(false);
      setUnitId('');
      setBlockId('');
      setSections(1);
      try {
        if (type === 'common') {
          const wt = await getAllWorkTypes();
          if (!cancelled) {
            setWorkTypes(Array.isArray(wt) ? wt.filter((w) => w.type === 'Common') : []);
          }
        } else if (type === 'independent') {
          const rows = await getUnitsByProjectId(projectId);
          const list = Array.isArray(rows) ? rows : rows?.data || [];
          if (!cancelled) setUnits(list);
        } else if (type === 'group') {
          const rows = await getAllBlocksByProject(projectId);
          const list = Array.isArray(rows) ? rows : rows?.data || [];
          if (!cancelled) setBlocks(list);
        }
      } catch (err) {
        if (!cancelled) setError(formatAiApiError(err) || 'Failed to load location options');
      } finally {
        if (!cancelled) setLoadingMeta(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, projectId, type]);

  useEffect(() => {
    if (
      !open ||
      type !== 'common' ||
      loadingMeta ||
      autoTried ||
      liveCreated ||
      !projectId ||
      !templateName
    ) {
      return;
    }
    // Auto-create when every applied Common work type is non-sectioned.
    if (sectionedCommon.length === 0 && (plainCommon.length > 0 || workTypeIds.length === 0)) {
      setAutoTried(true);
      void createCommon();
    }
  }, [
    open,
    type,
    loadingMeta,
    autoTried,
    liveCreated,
    sectionedCommon.length,
    plainCommon.length,
    projectId,
    templateName
  ]);

  const markCreated = () => {
    setLiveCreated(true);
    setStatus('Live tasks created');
    toast.success('Live tasks created');
  };

  const createCommon = async () => {
    setCreating(true);
    setError('');
    setStatus('Creating Common live tasks…');
    try {
      if (plainCommon.length || !sectionedCommon.length) {
        await createCommonLiveTasks({
          project_id: Number(projectId),
          name: templateName
        });
      }
      for (const wt of sectionedCommon) {
        setStatus(`Adding section for ${wt.name || wt.id}…`);
        await addSection({
          project_id: Number(projectId),
          workType_id: Number(wt.id),
          name: templateName
        });
      }
      markCreated();
    } catch (err) {
      setError(formatAiApiError(err) || 'Failed to create Common live tasks');
      setStatus('');
    } finally {
      setCreating(false);
    }
  };

  const createIndependent = async () => {
    if (!unitId) {
      setError('Select a unit');
      return;
    }
    setCreating(true);
    setError('');
    setStatus('Creating Independent live tasks…');
    try {
      await createIndependentLiveTasks({
        unit_id: Number(unitId),
        name: templateName
      });
      markCreated();
    } catch (err) {
      setError(formatAiApiError(err) || 'Failed to create Independent live tasks');
      setStatus('');
    } finally {
      setCreating(false);
    }
  };

  const createGroup = async () => {
    if (!blockId) {
      setError('Select a block');
      return;
    }
    setCreating(true);
    setError('');
    setStatus('Creating Group live tasks…');
    try {
      await createGroupLiveTasks({
        block_id: Number(blockId),
        name: templateName,
        sections: Math.max(1, Number(sections) || 1)
      });
      markCreated();
    } catch (err) {
      setError(formatAiApiError(err) || 'Failed to create Group live tasks');
      setStatus('');
    } finally {
      setCreating(false);
    }
  };

  if (!open) return null;

  // Templates are already applied here, so returning does not need to reopen the AI drawer.
  const returnTo = buildOpsAiReturnTo(location, projectId, { openAiChat: false });

  const openLiveWizard = () => {
    onClose?.({ created: liveCreated });
    navigateWithReturn(navigate, '/builder/operations/tasks', {
      returnTo,
      state: {
        projectID: Number(projectId),
        templateName,
        templateType: type,
        fromAiTemplate: true
      }
    });
  };

  const openDrawings = () => {
    onClose?.({ created: liveCreated });
    openBoqWithReturn(navigate, location, projectId, { openAiChat: false });
  };

  const createButton = () => {
    if (type === 'common') {
      return (
        <div className="space-y-2 text-slate-700">
          <p className="text-xs text-slate-500">
            {sectionedCommon.length
              ? `Creates Common tasks and adds ${sectionedCommon.length} section(s) for sectioned work types.`
              : 'Common (non-sectioned) live tasks can be created right away.'}
          </p>
          <button
            type="button"
            disabled={creating}
            onClick={createCommon}
            className="w-full rounded-lg bg-teal-700 px-3 py-2 text-xs font-semibold text-white disabled:opacity-60"
          >
            {creating ? 'Creating…' : 'Create Common live tasks'}
          </button>
        </div>
      );
    }
    if (type === 'independent') {
      return (
        <div className="space-y-2">
          <label className="block text-xs font-semibold text-slate-600">Unit</label>
          <select
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
            value={unitId}
            onChange={(e) => setUnitId(e.target.value)}
          >
            <option value="">Select unit…</option>
            {units.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name || u.unit_name || u.unitNo || `Unit ${u.id}`}
              </option>
            ))}
          </select>
          <button
            type="button"
            disabled={creating || !unitId}
            onClick={createIndependent}
            className="w-full rounded-lg bg-teal-700 px-3 py-2 text-xs font-semibold text-white disabled:opacity-60"
          >
            {creating ? 'Creating…' : 'Create Independent live tasks'}
          </button>
        </div>
      );
    }
    return (
      <div className="space-y-2">
        <label className="block text-xs font-semibold text-slate-600">Block</label>
        <select
          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
          value={blockId}
          onChange={(e) => setBlockId(e.target.value)}
        >
          <option value="">Select block…</option>
          {blocks.map((b) => (
            <option key={b.id} value={b.id}>
              {b.name || b.block_name || `Block ${b.id}`}
            </option>
          ))}
        </select>
        <label className="block text-xs font-semibold text-slate-600">Sections</label>
        <input
          type="number"
          min={1}
          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
          value={sections}
          onChange={(e) => setSections(e.target.value)}
        />
        <button
          type="button"
          disabled={creating || !blockId}
          onClick={createGroup}
          className="w-full rounded-lg bg-teal-700 px-3 py-2 text-xs font-semibold text-white disabled:opacity-60"
        >
          {creating ? 'Creating…' : 'Create Group live tasks'}
        </button>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-[90vh] w-full max-w-md flex-col rounded-xl bg-white shadow-xl">
        <div className="flex items-start justify-between border-b border-slate-100 px-4 py-3">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">Next steps</h3>
            <p className="mt-0.5 text-xs text-slate-500">
              Template “{templateName}” ({type})
            </p>
          </div>
          <button
            type="button"
            className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100"
            onClick={() => onClose?.({ created: liveCreated })}
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto px-4 py-4 text-sm">
          {loadingMeta ? (
            <p className="flex items-center gap-2 text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </p>
          ) : null}

          <Step done title="Templates applied" />

          <Step done={liveCreated} active={!liveCreated} title="Create live tasks">
            {liveCreated ? (
              <p className="text-xs text-slate-500">Done — tasks are live for this scope.</p>
            ) : (
              createButton()
            )}
          </Step>

          {needsDrawings ? (
            <Step active={liveCreated} title="Add drawings & BOQ / BOM">
              <div className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-900">
                <p>
                  {drawingCount} task{drawingCount > 1 ? 's' : ''} need drawings. Quantities and
                  materials are created in <span className="font-semibold">{bucket.label}</span> BOQ
                  for this project.
                </p>
                <p className="mt-1 text-[11px] text-sky-800">{bucket.hint}</p>
                <button
                  type="button"
                  onClick={openDrawings}
                  className="mt-2 rounded-md bg-sky-700 px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-sky-800"
                >
                  Open {bucket.label} BOQ
                </button>
              </div>
            </Step>
          ) : (
            <Step title="Add drawings & BOQ / BOM">
              <p className="text-xs text-slate-400">
                No task in this template is marked drawing-required, so no BOQ step is needed.
              </p>
            </Step>
          )}

          <Step active={liveCreated} title="Run on site">
            <p className="text-xs text-slate-500">
              Track progress in{' '}
              <button
                type="button"
                onClick={openLiveWizard}
                className="font-semibold text-slate-700 underline"
              >
                live tasks
              </button>
              .
            </p>
          </Step>

          {status ? <p className="text-xs text-teal-700">{status}</p> : null}
          {error ? <p className="text-xs text-rose-600">{error}</p> : null}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 px-4 py-3">
          <button
            type="button"
            onClick={openLiveWizard}
            className="rounded border border-slate-200 px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
          >
            Open live tasks wizard
          </button>
          <button
            type="button"
            onClick={() => onClose?.({ created: liveCreated })}
            className="rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-800"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
